import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import api from "@/lib/api";

const STEPS = [["landing", "Landing sessions"], ["ask_started", "Ask started"], ["ask_continued", "Continued conversation"],
  ["household_created", "Household created"], ["advisor_request", "Advisor requests"]];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", dateStyle: "medium", timeStyle: "short" }) + " ET" : "—");

const pct = (n, base) => (base ? `${Math.round((n / base) * 100)}% of landings` : "—");
const RANGES = [["7d", "Last 7 days"], ["30d", "Last 30 days"], ["all", "All time"]];

const RangeCard = ({ title, data, testid }) => (
  <section className="rounded-2xl border border-[#E5E7EB] bg-white p-5" data-testid={testid}>
    <h2 className="text-lg font-semibold text-[#0B192C]">{title}</h2>
    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
      {STEPS.map(([k, label]) => (
        <div key={k} className="rounded-xl bg-[#F7F6F2] p-3" data-testid={`${testid}-${k}`}>
          <p className="text-2xl font-semibold text-[#0B192C]">{data.totals[k]}</p>
          <p className="text-xs text-[#5B6670]">{label}</p>
          {k !== "landing" && <p className="text-[11px] text-[#1E3E62]" data-testid={`${testid}-${k}-rate`}>{pct(data.totals[k], data.totals.landing)}</p>}
        </div>
      ))}
    </div>
    <div className="mt-4 overflow-x-auto">
      <table className="w-full min-w-[640px] text-sm">
        <thead><tr className="text-left text-xs text-[#5B6670]"><th className="py-2 pr-3">Source · medium · campaign · content</th>{STEPS.map(([k, l]) => <th key={k} className="py-2 pr-3">{l}</th>)}</tr></thead>
        <tbody>
          {data.by_source.length === 0 && <tr><td colSpan={6} className="py-3 text-[#8A8F98]">No tracked activity in this period.</td></tr>}
          {data.by_source.map((r) => (
            <tr key={r.source} className="border-t border-[#EEF0F2]"><td className="py-2 pr-3 font-medium">{r.source}</td>{STEPS.map(([k]) => <td key={k} className="py-2 pr-3">{r[k]}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
    {data.qa_excluded > 0 && <p className="mt-2 text-xs text-[#5B6670]">{data.qa_excluded} QA/test events excluded.</p>}
  </section>
);

export default function HelmFunnel() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState("all");
  const load = useCallback(() => { setBusy(true); api.get("/admin/funnel", { params: { range } }).then((r) => setD(r.data)).catch(() => setD(null)).finally(() => setBusy(false)); }, [range]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-5" data-testid="helm-funnel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#0B192C]">SBA ad funnel</h1>
          <p className="text-sm text-[#5B6670]">Anonymous visits to /sba and what they did in Ask. Times in America/New_York.</p>
        </div>
        <button type="button" onClick={load} disabled={busy} className="inline-flex h-10 items-center gap-2 rounded-full border border-[#1E3E62] px-4 text-sm font-semibold text-[#1E3E62]" data-testid="funnel-refresh"><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />Refresh</button>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="funnel-range">
        {RANGES.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setRange(k)} className={`h-10 rounded-full px-4 text-sm font-semibold ${range === k ? "bg-[#0B192C] text-white" : "border border-[#D9D6CC] text-[#0B192C]"}`} data-testid={`funnel-range-${k}`}>{l}</button>
        ))}
      </div>
      {d && (
        <>
          <p className="text-xs text-[#5B6670]" data-testid="funnel-meta">Last updated {fmt(d.updated_at)} · Earliest tracked event {fmt(d.tracking_started)}{d.range_start ? ` · Showing since ${fmt(d.range_start)}` : " · Showing all stored events"}. No data exists before tracking started.</p>
          <RangeCard title={RANGES.find(([k]) => k === d.range)?.[1]} data={d.data} testid="funnel-selected" />
          <p className="text-xs text-[#5B6670]">Limits: untagged visits show as unknown / direct (not assumed paid). One count per visit per step. Ask steps count public Ask and plan-finder messages started from /sba or /ask; signed-in member chats aren't counted. Staff can't be detected automatically; QA visits use ?qa=1 or a utm value starting with "qa"/"test" and are excluded. Ad spend and clicks aren't included.</p>
        </>
      )}
    </div>
  );
}
